import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import PxGrid from 'components/layout/PxGrid';
import { Button, Grid, Icon, Popup, Table } from 'semantic-ui-react';
import { MedicamentDto, OrderItemDto, UpsertOrderAdministrationDto } from 'ts-types/api.types';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import { OrderAdministrationOrderStatus } from 'ts-types/api.enums';
import axios, { CancelTokenSource } from 'axios';
import { deliverOrder, editOrder, findOrderAdministrationById, sendOrder } from 'services/orderAdministrationServices';
import LoaderComponent from 'components/LoaderComponent';
import styled from 'styled-components';
import StyledActionButton from 'components/layout/StyledActionButton';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';
import { FormApi, FormState } from 'final-form';

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const OrderPostionsContainer = styled(PxGrid)`
  width: max-content;

  .order-positions-header {
    font-weight: bold;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
  }

`;

interface Props extends MessageSourceProps {
  order?: UpsertOrderAdministrationDto,
  orderId?: number,
  medicaments: Array<MedicamentDto>,
  onCancel: () => void
}

interface State {
  orderAdministration?: UpsertOrderAdministrationDto,
  orderItemData: Array<OrderItemDto>,
  orderItemsLoaded: boolean,
  isEdited: boolean,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class OrderPositionsModal extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      orderAdministration: props.order ? props.order : undefined,
      orderItemData: props.order ? props.order.orderItems : [],
      orderItemsLoaded: true,
      isEdited: false,
      cancelTokenSource
    }
  }

  loadRequiredData = async (orderId: number, cancelTokenSource: CancelTokenSource) => {

    try {
      const orderResponse = await findOrderAdministrationById(orderId, cancelTokenSource);

      this.setState({
        orderAdministration: orderResponse,
        orderItemData: orderResponse.orderItems
      });
    } finally {
      this.setState({
        orderItemsLoaded: true,
        isEdited: false
      });
    }
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  removeOrderItem = (orderItemId?: number) => {

    const {orderId} = this.props;
    const {orderItemData} = this.state;

    if (orderItemId && orderId) {

      this.setState({
        orderItemData: orderItemData.filter(oi => oi.id !== orderItemId),
        orderItemsLoaded: true
      });
    }
  }

  sendOrder = () => {

    const {orderId} = this.props;
    const {cancelTokenSource} = this.state;

    if (orderId) {

      this.setState({
        orderItemsLoaded: false
      });

      sendOrder(orderId, cancelTokenSource)
          .then(response => this.loadRequiredData(response.id, cancelTokenSource))
          .catch((e: any) => this.handleError(e.response.data))
          .finally(() => this.setState({
            orderItemsLoaded: true
          }))
    }
  }

  markAsDelivered = () => {

    const {orderId} = this.props;
    const {cancelTokenSource} = this.state;

    if (orderId) {

      this.setState({
        orderItemsLoaded: false
      });

      deliverOrder(orderId, cancelTokenSource)
          .then(response => this.loadRequiredData(response.id, cancelTokenSource))
          .catch((e: any) => this.handleError(e.response.data))
          .finally(() => this.setState({
            orderItemsLoaded: true
          }))
    }
  }

  onOrdered = (values: any, form: FormApi) => {

    const {orderItemData} = this.state;

    let newOrderItems = [...orderItemData];

    newOrderItems = newOrderItems.map(item => {
      return {...item, supplierQty: item.supplierQty = item.quantity}
    })

    form.change("orderItems", newOrderItems);
  }

  handleSubmit = async (values: any) => {
    const {orderId} = this.props;
    const {orderAdministration, cancelTokenSource} = this.state;

    if (orderId && orderAdministration) {
      const orderRequest = orderAdministration;
      orderRequest.orderItems = values.orderItems

      editOrder(orderId, orderRequest, cancelTokenSource)
          .then(response => this.loadRequiredData(orderId, cancelTokenSource))
          .catch((e) => this.handleError(e.response.data))
    }
  };

  handleChange = ({values}: FormState): void => {

    const {orderItemData} = this.state;

    if (values.orderItems !== orderItemData)
    this.setState({
      isEdited: true
    });
  };

  render(): React.ReactNode {

    const {orderItemData} = this.state;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={{
              orderItems: orderItemData
            }}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, submitting, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, medicaments, onCancel} = this.props;
    const {orderAdministration, orderItemData, orderItemsLoaded, isEdited} = this.state;

    const aOrderItemData = orderItemData ? orderItemData : [];

    return (
        <form onSubmit={handleSubmit}>
          <OrderPostionsContainer>
            <Grid.Row>
              <Grid.Column width={16}>
                {orderItemsLoaded && orderAdministration ?
                    <Table celled selectable size="small">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{getMessage('orderAdministration.medicament')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('orderAdministration.quantity')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('orderAdministration.supplierQty')}</Table.HeaderCell>
                          {orderAdministration.orderStatus !== OrderAdministrationOrderStatus.COMPLETED &&
                              <Table.HeaderCell>
                                {getMessage('orderAdministration.delete')}
                              </Table.HeaderCell>
                          }
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {
                          aOrderItemData.map((orderItem: OrderItemDto, index: number) => {
                            return (
                                <Table.Row key={index}>
                                  <Table.Cell width={5}>
                                    {medicaments.filter(m => m.id === orderItem.medicamentId)[0].description}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {orderItem.quantity}
                                  </Table.Cell>
                                  <Table.Cell width={2}>
                                    {orderAdministration.orderStatus === OrderAdministrationOrderStatus.SENT
                                        ? <Field
                                            style={{width: "70px"}}
                                            name={`orderItems[${index}].supplierQty`}
                                            component={Input}
                                            validate={composeValidators(mustBeNumber, mustBePositiveNumber, required)}
                                        />
                                        : orderItem.supplierQty
                                    }
                                  </Table.Cell>
                                  {orderAdministration.orderStatus !== OrderAdministrationOrderStatus.COMPLETED &&
                                      <Table.Cell width={1} textAlign="center">
                                        {this.renderDeleteActionButton(orderItem.id)}
                                      </Table.Cell>
                                  }
                                </Table.Row>
                            )
                          })
                        }
                      </Table.Body>
                    </Table>
                    : <LoaderComponent message={getMessage("orderAdministration.orders.loading")}/>
                }
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <FormButtonsRow>
                  {orderAdministration && orderAdministration.orderStatus === OrderAdministrationOrderStatus.SENT &&
                      <>
                        <Button
                            compact
                            type="button"
                            primary
                            disabled={orderItemData.length <= 0 || submitting}
                            onClick={this.markAsDelivered}
                        >
                          {getMessage("orderAdministration.markAsDelivered")}
                        </Button>
                        <Button
                            compact
                            type="button"
                            secondary
                            disabled={orderItemData.length <= 0 || submitting}
                            onClick={() => this.onOrdered(values, form)}
                        >
                          {getMessage("orderAdministration.onOrdered")}
                        </Button>

                        <Button
                            compact
                            color="orange"
                            type="button"
                            disabled={!isEdited || submitting}
                            onClick={() => this.handleSubmit(values)}
                        >
                          {getMessage("button.save")}
                        </Button>
                      </>
                  }
                  <Button
                      compact
                      type="button"
                      floated="right"
                      onClick={onCancel}
                  >
                    {getMessage("button.cancel")}
                  </Button>
                </FormButtonsRow>
              </Grid.Column>
            </Grid.Row>
            <FormSpy onChange={this.handleChange}/>
          </OrderPostionsContainer>
        </form>
    );
  }

  renderDeleteActionButton = (orderItemId: number): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <StyledActionButton icon
                            type="button"
        >
          <Popup
              trigger={<Icon name="trash"
                             onClick={() => this.removeOrderItem(orderItemId)}
              />
              }
              content={getMessage("msgDelete")}
              size="small"
              position="top center"
              style={PopupStyle}
              inverted
          />
        </StyledActionButton>
    );
  }
}

export default withMessages(OrderPositionsModal);
